import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "./Button.js";
import { EmptyState } from "./EmptyState.js";

/**
 * Catches render-time errors in the tree below it so a single thrown component
 * shows a recoverable fallback instead of unmounting the whole app to a blank
 * screen. This matters doubly for the desktop build, where the same React tree
 * *is* the application window — an unguarded throw there is a white void with
 * no address bar to escape from.
 *
 * Error boundaries have to be class components: there is no hook equivalent for
 * `getDerivedStateFromError` / `componentDidCatch`.
 */
export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Rendered instead of the default panel when the boundary trips. Receives the
   * error and a `reset` that clears the boundary so the subtree re-mounts.
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Notified on catch — wire to logging/telemetry. */
  onError?: (error: Error, info: ErrorInfo) => void;
  /**
   * When any value in this array changes, the boundary resets itself. Pass the
   * current route key so navigating away from a broken screen recovers without
   * a manual retry.
   */
  resetKeys?: unknown[];
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidUpdate(prev: ErrorBoundaryProps) {
    // A resetKeys change (typically the route) clears a tripped boundary so the
    // user is not stuck on the fallback after navigating elsewhere.
    if (this.state.error && !keysEqual(prev.resetKeys, this.props.resetKeys)) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <EmptyState
        icon="warning"
        title="Something went wrong on this screen"
        description="The rest of the app is still working. You can try this screen again, or move somewhere else."
        action={
          <Button variant="secondary" onClick={this.reset}>
            Try again
          </Button>
        }
      />
    );
  }
}

function keysEqual(a: unknown[] | undefined, b: unknown[] | undefined): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((value, index) => Object.is(value, b[index]));
}
